import React, { useState } from 'react';
import { 
  Package, TrendingUp, AlertTriangle, PlayCircle, Download, Truck,
  ShoppingCart, Factory, Warehouse, Database, BarChart3, Calendar,
  CheckCircle, Sparkles, Layers, FileText, Archive, TestTube
} from 'lucide-react';

const LogisticsSimplification = ({ project }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProcess, setSelectedProcess] = useState('otc');

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/lce/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_id: project?.id || 'demo-project',
          use_mock_data: true,
          include_ai_narratives: true
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const data = await response.json();
      setAnalysis(data);
      setActiveTab('overview');
    } catch (error) {
      console.error('Error running logistics conversion analysis:', error);
      alert('Failed to run analysis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    if (!analysis) return;
    
    const report = JSON.stringify(analysis, null, 2);
    const blob = new Blob([report], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logistics-conversion-${analysis.analysis_id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const processIcons = {
    otc: ShoppingCart,
    p2p: Package,
    inventory_wm: Warehouse,
    pp_mrp: Factory,
    le_tm: Truck
  };

  const processNames = {
    otc: 'Order-to-Cash (OTC)',
    p2p: 'Procure-to-Pay (P2P)',
    inventory_wm: 'Inventory & WM',
    pp_mrp: 'Production Planning & MRP',
    le_tm: 'Logistics Execution & TM'
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'footprint', label: 'Current Footprint', icon: Database },
    { id: 'simplification', label: 'Simplification Impact', icon: Layers },
    { id: 'risks', label: 'Risk Profile', icon: AlertTriangle },
    { id: 'mapping', label: 'Mapping & Blueprint', icon: FileText },
    { id: 'volumes', label: 'Volume & Archiving', icon: Archive },
    { id: 'backlog', label: 'Conversion Backlog', icon: CheckCircle },
    { id: 'waves', label: 'Wave Planning', icon: Calendar },
    { id: 'testing', label: 'Testing & Recon', icon: TestTube },
    { id: 'ai-insights', label: 'AI Insights', icon: Sparkles }
  ];

  if (!analysis) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-3xl font-bold mb-2">Logistics Conversion Engine</h2>
          <p className="text-gray-600 mb-6">
            Comprehensive MM/SD/PP/LE/WM/TM analysis for S/4HANA logistics conversion with AI-powered insights
          </p>
          <button
            onClick={runAnalysis}
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 disabled:opacity-50"
          >
            <PlayCircle className="w-5 h-5" />
            <span>{loading ? 'Running Analysis...' : 'Run Logistics Conversion Analysis'}</span>
          </button>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold mb-4">What This Module Analyzes:</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <ShoppingCart className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Order-to-Cash (OTC)</h4>
                <p className="text-sm text-gray-600">Sales orders, deliveries, billing, pricing, credit management</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Package className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Procure-to-Pay (P2P)</h4>
                <p className="text-sm text-gray-600">Purchase orders, vendors, info records, release strategies</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Warehouse className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Inventory & WM</h4>
                <p className="text-sm text-gray-600">MATDOC migration, stock types, WM to Stock Room/EWM</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Factory className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Production Planning (PP/MRP)</h4>
                <p className="text-sm text-gray-600">BOMs, routings, MRP strategies, production orders</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Truck className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Logistics Execution (LE/TM)</h4>
                <p className="text-sm text-gray-600">LE-TRA to TM/EWM migration, shipping, routes</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Database className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold">Business Partner Migration</h4>
                <p className="text-sm text-gray-600">Customer/vendor master data to BP conversion</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold mb-2">Logistics Conversion Engine</h2>
            <p className="text-gray-600">Analysis ID: {analysis.analysis_id}</p>
            <p className="text-sm text-gray-500">Generated: {new Date(analysis.generated_at).toLocaleString()}</p>
            {analysis.ai_narratives?.generated_by === 'GPT-5.1' && (
              <div className="mt-2 flex items-center space-x-2 text-sm text-purple-600">
                <Sparkles className="w-4 h-4" />
                <span>AI-powered insights by GPT-5.1</span>
              </div>
            )}
          </div>
          <button
            onClick={downloadReport}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <Package className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.total_materials.toLocaleString()}</div>
          <div className="text-sm opacity-90">Total Materials</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <Warehouse className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.total_plants}</div>
          <div className="text-sm opacity-90">Plants</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <Calendar className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.total_effort_months}M</div>
          <div className="text-sm opacity-90">Effort ({analysis.summary.total_effort_days} days)</div>
        </div>
        <div className={`bg-gradient-to-br rounded-xl p-6 text-white ${
          analysis.summary.overall_risk_level === 'CRITICAL' ? 'from-red-500 to-red-600' :
          analysis.summary.overall_risk_level === 'HIGH' ? 'from-orange-500 to-orange-600' :
          analysis.summary.overall_risk_level === 'MEDIUM' ? 'from-yellow-500 to-yellow-600' :
          'from-green-500 to-green-600'
        }`}>
          <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
          <div className="text-3xl font-bold mb-2">{analysis.summary.overall_risk_level}</div>
          <div className="text-sm opacity-90">Risk Level ({analysis.summary.total_risks} risks)</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-md">
        <div className="border-b overflow-x-auto">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-green-600 text-green-600 font-semibold'
                      : 'border-transparent text-gray-600 hover:text-green-600 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {analysis.summary.total_customers.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Customers</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {analysis.summary.total_vendors.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Vendors</div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {analysis.summary.wm_active ? 'Active' : 'Inactive'}
                  </div>
                  <div className="text-sm text-gray-600">WM Status</div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4">Conversion Summary</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.total_simplification_impacts}</div>
                    <div className="text-sm text-gray-600">Simplification Impacts</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.total_waves}</div>
                    <div className="text-sm text-gray-600">Conversion Waves</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.total_test_scenarios}</div>
                    <div className="text-sm text-gray-600">Test Scenarios</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-700">{analysis.summary.annual_data_growth_gb} GB</div>
                    <div className="text-sm text-gray-600">Annual Data Growth</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Footprint Tab */}
          {activeTab === 'footprint' && (
            <div className="space-y-6">
              <div className="flex space-x-2 overflow-x-auto pb-2">
                {Object.keys(analysis.current_logistics_model).filter(key => !['model_id', 'generated_at', 'complexity_score', 'data_quality_score', 'open_documents'].includes(key)).map((process) => (
                  <button
                    key={process}
                    onClick={() => setSelectedProcess(process)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                      selectedProcess === process
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {processNames[process] || process}
                  </button>
                ))}
              </div>

              {selectedProcess && analysis.current_logistics_model[selectedProcess] && (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(analysis.current_logistics_model[selectedProcess]).map(([key, value]) => (
                      <div key={key} className="p-4 border-2 border-gray-200 rounded-lg">
                        <div className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                        <div className="text-xl font-bold text-gray-800">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                           Array.isArray(value) ? value.join(', ') :
                           typeof value === 'object' ? JSON.stringify(value).slice(0, 50) + '...' :
                           value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <h4 className="font-bold mb-3">Open Documents</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(analysis.current_logistics_model.open_documents).map(([doc, count]) => (
                    <div key={doc} className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                      <div className="text-sm text-gray-600 capitalize">{doc.replace(/_/g, ' ')}</div>
                      <div className="text-2xl font-bold text-orange-600">{count.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Simplification Impact Tab */}
          {activeTab === 'simplification' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{analysis.simplification_impact.impacts_by_level.critical || 0}</div>
                  <div className="text-sm text-gray-600">Critical Impact</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{analysis.simplification_impact.impacts_by_level.high}</div>
                  <div className="text-sm text-gray-600">High Impact</div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{analysis.simplification_impact.impacts_by_level.medium}</div>
                  <div className="text-sm text-gray-600">Medium Impact</div>
                </div>
              </div>

              <div className="space-y-3">
                {analysis.simplification_impact.impacts.map((impact, index) => (
                  <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-400">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold">{impact.title}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        impact.impact_level === 'critical' ? 'bg-red-100 text-red-800' :
                        impact.impact_level === 'high' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {impact.impact_level}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{impact.description}</p>
                    <div className="flex space-x-4 text-xs text-gray-500">
                      <span>Area: {impact.area}</span>
                      <span>Effort: {impact.effort_days} days</span>
                      <span>Risk: {impact.risk}</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-2">Business Impact: {impact.business_impact}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Risk Profile Tab */}
          {activeTab === 'risks' && (
            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg">
                <h4 className="font-bold mb-2">Overall Risk Assessment</h4>
                <div className="flex items-center space-x-4">
                  <div className="text-4xl font-bold text-red-600">{analysis.risk_profile.overall_risk_score}</div>
                  <div>
                    <p className="font-semibold">{analysis.risk_profile.overall_risk_level} Risk Level</p>
                    <p className="text-sm text-gray-600">Based on {analysis.risk_profile.total_risks} identified risks</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{analysis.risk_profile.risks_by_severity.critical}</div>
                  <div className="text-sm text-gray-600">Critical</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{analysis.risk_profile.risks_by_severity.high}</div>
                  <div className="text-sm text-gray-600">High</div>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{analysis.risk_profile.risks_by_severity.medium}</div>
                  <div className="text-sm text-gray-600">Medium</div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{analysis.risk_profile.risks_by_severity.low}</div>
                  <div className="text-sm text-gray-600">Low</div>
                </div>
              </div>

              <div className="space-y-2">
                {analysis.risk_profile.risks.map((risk, index) => (
                  <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-red-400">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold">{risk.title}</span>
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                        Score: {risk.risk_score}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{risk.description}</p>
                    <div className="flex space-x-4 text-xs text-gray-600">
                      <span>Category: {risk.category}</span>
                      <span>Likelihood: {risk.likelihood}/5</span>
                      <span>Impact: {risk.impact}/5</span>
                      <span className="capitalize">Severity: {risk.severity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mapping & Blueprint Tab */}
          {activeTab === 'mapping' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{analysis.mapping_rules.total_mapping_areas}</div>
                  <div className="text-sm text-gray-600">Mapping Areas</div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{analysis.mapping_rules.total_rules}</div>
                  <div className="text-sm text-gray-600">Total Rules</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600">{analysis.mapping_rules.high_complexity_rules}</div>
                  <div className="text-sm text-gray-600">High Complexity</div>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-3">Conversion Blueprint Sections</h4>
                <div className="space-y-3">
                  {analysis.conversion_blueprint.blueprint_sections.map((section, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-bold">{section.section_name}</h5>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          section.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          section.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {section.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Effort: {section.effort_days} days</p>
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-gray-700 mb-1">Prerequisites:</p>
                        <div className="flex flex-wrap gap-1">
                          {section.prerequisites.map((prereq, i) => (
                            <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">{prereq}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">Activities:</p>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {section.activities.slice(0, 3).map((activity, i) => (
                            <li key={i}>• {activity}</li>
                          ))}
                          {section.activities.length > 3 && (
                            <li className="text-gray-500 italic">+ {section.activities.length - 3} more...</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Volume & Archiving Tab */}
          {activeTab === 'volumes' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-bold mb-4">MATDOC Volume Estimates</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {analysis.volume_estimates.matdoc_volume.annual_matdoc_entries.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Annual MATDOC Entries</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {analysis.volume_estimates.matdoc_volume.annual_growth_gb} GB
                    </div>
                    <div className="text-sm text-gray-600">Annual Growth</div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-4">Archiving Recommendations</h4>
                <div className="space-y-3">
                  {analysis.archiving_recommendations.recommendations.map((rec, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-bold">{rec.document_type}</h5>
                        <span className="text-sm text-gray-600">Effort: {rec.effort_days} days</span>
                      </div>
                      <div className="grid md:grid-cols-2 gap-2 mb-2 text-sm">
                        <div>
                          <span className="text-gray-600">Current Open: </span>
                          <span className="font-semibold">{rec.current_open_count.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Recommended Closure: </span>
                          <span className="font-semibold text-green-600">{rec.recommended_closure.toLocaleString()}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">Criteria:</p>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {rec.archiving_criteria.map((criteria, i) => (
                            <li key={i}>• {criteria}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Backlog Tab */}
          {activeTab === 'backlog' && (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg mb-4">
                <h4 className="font-bold mb-2">Conversion Backlog Summary</h4>
                <p className="text-sm text-gray-600">
                  {analysis.conversion_backlog.length} work items
                </p>
              </div>

              <div className="space-y-2">
                {analysis.conversion_backlog.map((item, index) => (
                  <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-400">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <span className="font-semibold">{item.title}</span>
                        <p className="text-xs text-gray-500 mt-1">{item.backlog_id}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        item.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {item.priority}
                      </span>
                    </div>
                    <div className="flex space-x-4 text-sm text-gray-600">
                      <span>Area: {item.area}</span>
                      <span>Effort: {item.effort_days} days</span>
                      <span className="capitalize">Complexity: {item.complexity}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Waves Tab */}
          {activeTab === 'waves' && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg mb-4">
                <h4 className="font-bold mb-2">6-Wave Logistics Conversion Plan</h4>
                <p className="text-sm text-gray-600">
                  Total duration: {analysis.conversion_waves.reduce((sum, w) => sum + w.duration_weeks, 0)} weeks
                </p>
              </div>

              {analysis.conversion_waves.map((wave, index) => (
                <div key={index} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                  <div className={`p-4 bg-gradient-to-r ${
                    index % 3 === 0 ? 'from-green-500 to-green-600' :
                    index % 3 === 1 ? 'from-blue-500 to-blue-600' :
                    'from-purple-500 to-purple-600'
                  } text-white`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-lg">Wave {wave.wave}: {wave.name}</h4>
                        <p className="text-sm opacity-90 mt-1">
                          Duration: {wave.duration_weeks} weeks · {wave.backlog_items} items · {wave.total_effort_days} effort days
                        </p>
                      </div>
                      <Calendar className="w-8 h-8 opacity-80" />
                    </div>
                  </div>
                  {wave.tasks.length > 0 && (
                    <div className="p-4 bg-gray-50">
                      <h5 className="font-semibold mb-2 text-sm">Tasks in this wave:</h5>
                      <div className="space-y-1">
                        {wave.tasks.map((task, taskIndex) => (
                          <div key={taskIndex} className="text-sm text-gray-600">
                            • {task.title} ({task.effort_days} days)
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Testing & Reconciliation Tab */}
          {activeTab === 'testing' && (
            <div className="space-y-6">
              <div>
                <h4 className="font-bold mb-4">Reconciliation Points</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {analysis.reconciliation_plan.reconciliation_points.map((point, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg">
                      <div className="flex items-start space-x-2 mb-2">
                        {point.critical && <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />}
                        <h5 className="font-semibold">{point.title}</h5>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Type: {point.type}</p>
                        <p>Frequency: {point.frequency}</p>
                        <p className={`inline-block px-2 py-1 rounded text-xs ${
                          point.complexity === 'high' ? 'bg-red-100 text-red-800' :
                          point.complexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {point.complexity} complexity
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-4">Test Scenarios</h4>
                <div className="space-y-2">
                  {analysis.test_scenarios.map((scenario, index) => (
                    <div key={index} className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-400">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <span className="font-semibold">{scenario.title}</span>
                          <p className="text-xs text-gray-500 mt-1">{scenario.scenario_id}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          scenario.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          scenario.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {scenario.priority}
                        </span>
                      </div>
                      <div className="flex space-x-4 text-sm text-gray-600 mb-2">
                        <span>Process: {scenario.process}</span>
                        <span>Type: {scenario.test_type}</span>
                        <span>{scenario.test_cases} test cases</span>
                        <span>{scenario.estimated_hours} hours</span>
                      </div>
                      {scenario.steps && (
                        <div className="mt-2">
                          <p className="text-xs font-semibold text-gray-700 mb-1">Steps:</p>
                          <div className="flex flex-wrap gap-1">
                            {scenario.steps.map((step, i) => (
                              <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded">{step}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* AI Insights Tab */}
          {activeTab === 'ai-insights' && analysis.ai_narratives && (
            <div className="space-y-6">
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg flex items-center space-x-3">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <div>
                  <h4 className="font-bold">AI-Generated Logistics Insights</h4>
                  <p className="text-sm text-gray-600">
                    Powered by {analysis.ai_narratives.generated_by} · Generated {new Date(analysis.ai_narratives.generated_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                  <span>Business Summary</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.business_summary}
                </div>
              </div>

              <div className="bg-white border-2 border-purple-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <Warehouse className="w-6 h-6 text-purple-600" />
                  <span>WM Migration Recommendation</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.wm_recommendation}
                </div>
              </div>

              <div className="bg-white border-2 border-red-200 rounded-lg p-6">
                <h4 className="font-bold text-xl mb-4 flex items-center space-x-2">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <span>Top Logistics Risks</span>
                </h4>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                  {analysis.ai_narratives.top_risks}
                </div>
              </div>

              {analysis.ai_narratives.context_used && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-semibold mb-2 text-sm">Context Used for AI Generation:</h5>
                  <div className="grid md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Total Materials</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.total_materials.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">WM Active</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.wm_active ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Total Impacts</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.total_impacts}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Risk Level</p>
                      <p className="font-bold">{analysis.ai_narratives.context_used.risk_level}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogisticsSimplification;